import * as React from 'react';
import { AuthStatus } from 'constants/auth';
import useViewer from 'hooks/useViewer';
import { getIsNativePlatform } from 'utils/client/mobile';
import SidebarPage from './SidebarPage';
import type { Props as SidebarPageProps } from './props';
import type { GetLayout } from 'types/page';

export const DEFAULT_SIDEBAR_LAYOUT_PROPS: SidebarPageProps = {
  hideFooter: true,
  hideNav: true,
};

// For dashboards that fill the screen and scroll their own panels (the flow pages). Their
// content below the screen, such as the SEO-only flow description, stays clipped.
export const SCREEN_HEIGHT_SIDEBAR_LAYOUT_PROPS: SidebarPageProps = {
  ...DEFAULT_SIDEBAR_LAYOUT_PROPS,
  isScreenHeight: true,
};

export const HEATMAP_SIDEBAR_LAYOUT_PROPS: SidebarPageProps = {
  hideNav: true,
};

type SidebarLayoutVariant = 'default' | 'optionsProfitCalculator';

type SidebarLayoutProps = SidebarPageProps & {
  variant?: SidebarLayoutVariant;
};

function SidebarLayout({
  children,
  variant = 'default',
  ...props
}: React.PropsWithChildren<SidebarLayoutProps>) {
  const viewer = useViewer();
  const isMobile = process.env.TARGET_PLATFORM === 'MOBILE' || getIsNativePlatform();
  const isAnonymous = viewer.status === AuthStatus.Anonymous;
  const layoutProps =
    variant === 'optionsProfitCalculator'
      ? {
          hideFooter: true,
          hideNav: !isAnonymous || isMobile,
        }
      : props;

  return <SidebarPage {...layoutProps}>{children}</SidebarPage>;
}

export function withSidebarLayout(
  props: SidebarPageProps = DEFAULT_SIDEBAR_LAYOUT_PROPS,
): GetLayout {
  return (page) => <SidebarLayout {...props}>{page}</SidebarLayout>;
}

export function withOptionsProfitCalculatorSidebarLayout(): GetLayout {
  return (page) => <SidebarLayout variant="optionsProfitCalculator">{page}</SidebarLayout>;
}
